import { type ActionPattern, delay, fork } from "redux-saga/effects";
import { call, put, take, takeEvery } from "typed-redux-saga";
import { getToolControllerForAnnotationTool } from "viewer/controller/combinations/tool_controls";
import getSceneController from "viewer/controller/scene_controller_provider";
import { AnnotationTool, MeasurementTools, Toolkit } from "viewer/model/accessors/tool_accessor";
import {
  type CycleToolAction,
  hideMeasurementTooltipAction,
  type SetToolAction,
  setIsMeasuringAction,
} from "viewer/model/actions/ui_actions";
import { getNextTool } from "viewer/model/reducers/reducer_helpers";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select } from "viewer/model/sagas/effect_generators";
import { getDisabledInfoForTools } from "../accessors/disabled_tool_accessor";
import { Toolkits } from "../accessors/tool_accessor";
import type { Action } from "../actions/actions";
import { updateUserSettingAction } from "../actions/settings_actions";
import { setToolAction } from "../actions/ui_actions";
import { ensureWkInitialized } from "./ready_sagas";

function* ensureActiveToolIsInToolkit() {
  const activeToolkit = yield* select((state) => state.userConfiguration.activeToolkit);
  const activeTool = yield* select((state) => state.uiInformation.activeTool);
  const availableTools = Toolkits[activeToolkit];

  if (!availableTools.includes(activeTool)) {
    yield put(setToolAction(availableTools[0]));
  }
}

function* switchAwayFromDisabledTool(): Saga<never> {
  // This saga ensures that if the current tool becomes disabled,
  // another tool is automatically selected. Should the old tool
  // become available again (without the user having switched
  // to another tool), the old tool will be re-activated.
  let lastForcefullyDisabledTool: AnnotationTool | null = null;

  let disabledInfosForTools = yield* select(getDisabledInfoForTools);
  let activeTool = yield* select((state) => state.uiInformation.activeTool);
  while (true) {
    // Check the active tool and the disabled info object to decide
    // whether
    // - to switch to MOVE tool(because the current tool is disabled) or
    // - to switch back to a previous tool which got available again
    // - to forget about the previous tool (when the user changed the tool
    //   on their own) to avoid unexpected tool switching.
    const disabledInfoForCurrentTool = disabledInfosForTools[activeTool.id];
    const isLastForcefullyDisabledToolAvailable =
      lastForcefullyDisabledTool != null &&
      !disabledInfosForTools[lastForcefullyDisabledTool.id].isDisabled;

    if (disabledInfoForCurrentTool.isDisabled) {
      lastForcefullyDisabledTool = activeTool;
      yield put(setToolAction(AnnotationTool.MOVE));
    } else if (
      lastForcefullyDisabledTool != null &&
      isLastForcefullyDisabledToolAvailable &&
      activeTool === AnnotationTool.MOVE
    ) {
      // Re-enable the tool that was disabled before.
      yield put(setToolAction(lastForcefullyDisabledTool));
      lastForcefullyDisabledTool = null;
    } else if (activeTool !== AnnotationTool.MOVE) {
      // Forget the last disabled tool as another tool besides the move tool was selected.
      lastForcefullyDisabledTool = null;
    }

    // Listen to actions and wait until the getDisabledInfoForTools return value changed
    let continueWaiting = true;
    while (continueWaiting) {
      yield take();
      // Debounce so that we don't process ALL actions.
      yield delay(50);
      const newActiveTool = yield* select((state) => state.uiInformation.activeTool);
      if (newActiveTool !== activeTool) {
        activeTool = newActiveTool;
        continueWaiting = false;
      }
      const newDisabledInfosForTools = yield* select(getDisabledInfoForTools);
      if (newDisabledInfosForTools !== disabledInfosForTools) {
        disabledInfosForTools = newDisabledInfosForTools;
        continueWaiting = false;
      }
    }
    // The active tool or the disabled info changed. Start from the beginning.
  }
}

export function* watchToolDeselection(): Saga<never> {
  let previousTool = yield* select((state) => state.uiInformation.activeTool);

  while (true) {
    const action = (yield* take(["SET_TOOL", "CYCLE_TOOL"]) as any) as
      | SetToolAction
      | CycleToolAction;
    const storeState = yield* select((state) => state);
    let executeDeselect = false;

    if (action.type === "SET_TOOL") {
      executeDeselect = true;
    } else if (getNextTool(storeState) != null) {
      executeDeselect = true;
    }

    if (executeDeselect) {
      getToolControllerForAnnotationTool(previousTool).onToolDeselected();
    }

    previousTool = storeState.uiInformation.activeTool;
  }
}

function* watchToolReset(): Saga<never> {
  while (true) {
    yield* take("ESCAPE");
    const activeTool = yield* select((state) => state.uiInformation.activeTool);
    if (MeasurementTools.includes(activeTool)) {
      const sceneController = yield* call(getSceneController);
      const geometry =
        activeTool === AnnotationTool.AREA_MEASUREMENT
          ? sceneController.areaMeasurementGeometry
          : sceneController.lineMeasurementGeometry;
      geometry.hide();
      geometry.reset();
      yield* put(hideMeasurementTooltipAction());
      yield* put(setIsMeasuringAction(false));
    } else if (activeTool === AnnotationTool.VOXEL_PIPETTE) {
      yield* put(hideMeasurementTooltipAction());
    }
  }
}

function* updateToolTimestamp(setToolAction: SetToolAction): Saga<void> {
  const newTool = setToolAction.tool;
  const toolTimestamps = yield* select((state) => state.userConfiguration.timestampsForTools);
  const updatedTimestamps = {
    ...toolTimestamps,
    [newTool.id]: Date.now(),
  };
  yield* put(updateUserSettingAction("timestampsForTools", updatedTimestamps));
}

function* rememberToolPreferences({ tool }: SetToolAction): Saga<void> {
  switch (tool.id) {
    case AnnotationTool.BRUSH.id:
    case AnnotationTool.TRACE.id:
      yield* put(updateUserSettingAction("writePreference", tool.id));
      return;
    case AnnotationTool.ERASE_BRUSH.id:
    case AnnotationTool.ERASE_TRACE.id:
      yield* put(updateUserSettingAction("erasePreference", tool.id));
      return;
    case AnnotationTool.LINE_MEASUREMENT.id:
    case AnnotationTool.AREA_MEASUREMENT.id:
      yield* put(updateUserSettingAction("measurementPreference", tool.id));
      return;
    default:
      return;
  }
}

export default function* toolSaga() {
  yield* call(ensureWkInitialized);

  const isViewMode = yield* select((state) => state.annotation.annotationType === "View");
  if (isViewMode) {
    yield* put(updateUserSettingAction("activeToolkit", Toolkit.ALL_TOOLS));
  }

  yield fork(watchToolDeselection);
  yield fork(watchToolReset);
  yield fork(switchAwayFromDisabledTool);
  yield* takeEvery(
    [
      (action: Action) =>
        action.type === "UPDATE_USER_SETTING" && action.propertyName === "activeToolkit",
    ] as ActionPattern,
    ensureActiveToolIsInToolkit,
  );
  yield* takeEvery("SET_TOOL", updateToolTimestamp);
  yield* takeEvery("SET_TOOL", rememberToolPreferences);
}
